/**
 * Routing for an EDIT-mode save on the family day panel.
 *
 * One (day, mealtime, campus) sitting can hold SEVERAL placed batches — the
 * checkout writes one batch per household, and a household that ordered its
 * children on separate visits ends up with one batch per child. The edit
 * panel collects desired lines across every child on the campus, so it must
 * post each line back to the batch that actually holds that child's meals.
 *
 * Sending a child's line to a SIBLING's batch is not merely untidy: the
 * server's `replaceOrder` excludes only the batch it is rewriting from the
 * per-sitting subsidy allowance, so the misrouted line sees the child's own
 * live sponsored meal in a batch it did NOT exclude, spends the allowance,
 * and re-prices the meal at full family price. At a 100%-sponsored school
 * that turns a $0 swap into a wallet debit the family can never cover
 * (PROD-1295).
 */

import type { FamilyOrderWire } from '@/api/family-orders'
import type { Mealtime } from '@/api/school-orders'

/** A line the parent wants on the sitting after the edit. */
export interface DesiredOrderLine {
  studentUserId: string
  accountId: string
  menuItemId: string
  quantity: number
  specialDietAccommodationId?: string | null
}

export interface EditCommitPlan {
  /** Existing batches to rewrite. An EMPTY `items` cancels that batch. */
  replaces: Array<{ orderBatchId: string; items: DesiredOrderLine[] }>
  /** Lines for children with no batch on this sitting — placed as new orders. */
  creates: Array<{ accountId: string; items: DesiredOrderLine[] }>
}

/**
 * Split `desired` across the sitting's placed batches.
 *
 * A line goes to the batch that already holds a line for the SAME child;
 * children with no batch here are grouped by billing account into new
 * orders. Every placed batch appears in `replaces` even when nothing routes
 * to it — an empty item list is how a full removal is expressed, and
 * omitting the batch would silently strand the meals it still holds.
 */
export function planEditCommit(args: {
  /** The sitting's placed batches, in wire order (see `batchesForSitting`). */
  batches: ReadonlyArray<FamilyOrderWire>
  desired: ReadonlyArray<DesiredOrderLine>
}): EditCommitPlan {
  const sharedStudents = new Set(
    args.batches
      .filter((b) => b.sharedReadOnly)
      .flatMap((b) => b.items.map((item) => item.studentUserId).filter((id): id is string => Boolean(id))),
  )
  const writableBatches = args.batches.filter((b) => !b.sharedReadOnly)
  const batchByStudent = new Map<string, string>()
  for (const batch of writableBatches) {
    for (const item of batch.items) {
      // First batch holding the child wins — stable, and a child's meals for
      // one sitting live in one batch in every shape the checkout writes.
      if (item.studentUserId && !batchByStudent.has(item.studentUserId)) {
        batchByStudent.set(item.studentUserId, batch.orderBatchId)
      }
    }
  }

  const itemsByBatch = new Map<string, DesiredOrderLine[]>(writableBatches.map((b) => [b.orderBatchId, []]))
  const itemsByAccount = new Map<string, DesiredOrderLine[]>()
  for (const line of args.desired) {
    if (sharedStudents.has(line.studentUserId)) {
      continue
    }
    const orderBatchId = batchByStudent.get(line.studentUserId)
    const bucket = orderBatchId ? itemsByBatch.get(orderBatchId) : undefined
    if (bucket) {
      bucket.push(line)
      continue
    }
    const forAccount = itemsByAccount.get(line.accountId) ?? []
    forAccount.push(line)
    itemsByAccount.set(line.accountId, forAccount)
  }

  return {
    replaces: Array.from(itemsByBatch, ([orderBatchId, items]) => ({ orderBatchId, items })),
    creates: Array.from(itemsByAccount, ([accountId, items]) => ({ accountId, items })),
  }
}

/**
 * The day's placed, non-voided batches at one campus, grouped by mealtime —
 * a LIST per mealtime, because one sitting can hold one batch per child.
 * VOIDED batches are dropped server-side too; the filter here is defensive
 * against a stale cache driving receipt mode off a just-cancelled order.
 */
export function batchesByMealtimeForDay(
  orders: ReadonlyArray<FamilyOrderWire>,
  scope: { dateKey: string; serviceLocationId: string },
): Map<Mealtime, FamilyOrderWire[]> {
  const map = new Map<Mealtime, FamilyOrderWire[]>()
  for (const order of orders) {
    if (
      order.status === 'VOIDED' ||
      order.serviceDate.slice(0, 10) !== scope.dateKey ||
      order.serviceLocationId !== scope.serviceLocationId
    ) {
      continue
    }
    const bucket = map.get(order.mealtime) ?? []
    bucket.push(order)
    map.set(order.mealtime, bucket)
  }
  return map
}
